import { useEffect, useMemo, useRef, useState } from 'react';
import { Trophy, MapPin, Medal, Clock, Check, Plus, X } from 'lucide-react';
import { courtDisplayName, getTournamentType } from '../../../engine';
import {
  memberBookings,
  memberSingleMatches,
  splitUpcomingPast,
  relativeDayLabel,
  matchSideOf,
  isMatchCompleted,
  isMemberInMatch,
  planOpenSlots,
  planSetSlot,
  type LiveTournament,
} from '../memberData';
import { Card, EmptyState, Segments, lastNameOf, PlayerName, TeamNames } from '../mobileUi';
import { useLocale, useT } from '../../../i18n';
import { MemberPicker } from '../MemberPicker';
import { MobileSeriespill } from './MobileSeriespill';
import { AcrossClubsMatches } from '../AcrossClubs';
import { resultFingerprint } from '../../../state/resultFingerprint';
import { useUnsavedMatchScore } from '../../../state/unsavedScores';

export interface MobileMatchesProps {
  member: any;
  bookings: any[];
  plannedMatches: any[];
  singleMatches: any[];
  activities: any[];
  effectivePlayerDB: any[];
  /** getPlayerTournamentHistory(member) over archiveWithLive — computed once in MobileApp. */
  history: any[];
  live: LiveTournament[];
  /** Seriespill lives inside Kamper behind the «Seriespill» switch. */
  leagueLive?: LiveTournament[];
  leaguePast?: any[];
  courtMeta?: any;
  calcPlayerPositionInTournament: (playerId: any, t: any) => any;
  recordResultForTournament?: (
    tid: any,
    matchId: any,
    score1: number,
    score2: number,
    source: string,
  ) => void;
  recordKMResultForTournament?: (tid: any, updatedMatch: any) => void;
  /** Fill / empty the slots of a planned single match you are in (usePlannedMatchActions). */
  updatePlannedMatchPlayers?: (id: any, teams: { team1: any; team2: any }) => Promise<any>;
  /** Record the result of a planned single match (sets → single match + ELO, plan removed). */
  completePlannedMatch?: (id: any, data: any) => Promise<any>;
  /** Open on this segment (e.g. «live» from the home card). */
  initialSegment?: string | null;
  /**
   * Offer «Alle klubber» — the account's own single matches in every club
   * (#760). Only for an account on the player surface.
   */
  acrossClubs?: boolean;
}

type Seg = 'upcoming' | 'played' | 'all';

// «Kamper» — four segments: tournaments in progress (with result entry for
// the member's own matches), planned single matches on booked courts, played
// single matches with the member's ELO change, and the tournament history
// (same engine helpers as the desktop profile modal, so placements agree).
export function MobileMatches({
  member,
  bookings,
  plannedMatches,
  singleMatches,
  activities,
  effectivePlayerDB,
  history,
  live,
  leagueLive = [],
  leaguePast = [],
  courtMeta,
  calcPlayerPositionInTournament,
  recordResultForTournament,
  recordKMResultForTournament,
  updatePlannedMatchPlayers,
  completePlannedMatch,
  initialSegment = null,
  acrossClubs = false,
}: MobileMatchesProps) {
  // `tr`, not `t`: this file binds `t` to a tournament in three places.
  const tr = useT();
  const [seg, setSeg] = useState<Seg>('upcoming');
  // «Alle andre» (tournaments + single matches, the segments below) or
  // «Seriespill» (its own screen, embedded).
  const [mode, setMode] = useState<'tournaments' | 'league'>(
    initialSegment === 'league' ? 'league' : 'tournaments',
  );
  useEffect(() => {
    if (initialSegment === 'live') {
      setMode('tournaments');
      setSeg('upcoming');
    }
    if (initialSegment === 'league') setMode('league');
  }, [initialSegment]);
  const leagueOpen = leagueLive.reduce((s, l) => s + l.open.length, 0);
  const db = useMemo(() => effectivePlayerDB || [], [effectivePlayerDB]);
  // Filling the open slots of your own planned match: which slot is being picked.
  const [pickSlot, setPickSlot] = useState<{
    planId: any;
    team: 1 | 2;
    pos: 'p1Id' | 'p2Id';
  } | null>(null);
  const [busyPlan, setBusyPlan] = useState<any>(null);
  const setSlot = async (plan: any, team: 1 | 2, pos: 'p1Id' | 'p2Id', playerId: any | null) => {
    if (!updatePlannedMatchPlayers) return;
    setBusyPlan(plan.id);
    try {
      await updatePlannedMatchPlayers(plan.id, planSetSlot(plan, team, pos, playerId));
    } finally {
      setBusyPlan(null);
    }
  };
  // Surnames in line-ups (like the KM draw sheet); open slots read «ledig».
  const fn = (id: any) => (id == null ? tr('mobile.common.openSlot') : lastNameOf(db, id, tr));

  const planned = useMemo(
    () =>
      splitUpcomingPast(
        memberBookings({ bookings, plannedMatches, activities, member }),
      ).upcoming.filter((b) => b.plan),
    [bookings, plannedMatches, activities, member],
  );
  const played = useMemo(
    () => memberSingleMatches(singleMatches, member?.id),
    [singleMatches, member],
  );
  const past = useMemo(() => {
    if (!member) return [];
    return (history || [])
      .filter((h: any) => !(h.tournament?.isLive && h.tournament?.status !== 'done'))
      .map((h: any) => {
        const t = h.tournament || {};
        const pos = h.finalPosition ?? calcPlayerPositionInTournament(member.id, t);
        return {
          key: h.key,
          name: t.config?.name || t.name || tr('mobile.common.tournament'),
          tt: getTournamentType(t.config || t.type),
          when: t.archivedAt || t.completedAt || t.createdAt || 0,
          position: typeof pos === 'number' ? pos : (pos?.position ?? null),
          total: typeof pos === 'object' && pos ? pos.total : null,
          matches: (h.matches || []).length,
        };
      })
      .sort((a: any, b: any) => (b.when || 0) - (a.when || 0));
    // `tr` IS a real dependency: the fallback name «Turnering» is built here,
    // so without it a language switch would leave this list in the old one
    // until something else invalidated the memo. `t` changes identity with
    // the locale, which is exactly the signal wanted.
  }, [member, history, calcPlayerPositionInTournament, tr]);

  const openCount = live.reduce((s, l) => s + l.open.length, 0);
  // «Spilt» = played single matches and finished tournaments, newest first.
  const playedAll = useMemo(
    () =>
      [
        ...played.map((pm) => ({ kind: 'single' as const, ts: pm.playedAt || 0, pm })),
        ...past.map((h: any) => ({ kind: 'tournament' as const, ts: h.when || 0, h })),
      ].sort((a, b) => b.ts - a.ts),
    [played, past],
  );

  return (
    <div className="px-4 pb-4 space-y-3">
      {/* Heading + the single «Seriespill» toggle: off = everything else (the segments) */}
      <div className="flex items-end justify-between gap-2 pt-2">
        <h1 className="font-display font-semibold text-ink leading-tight" style={{ fontSize: 26 }}>
          {tr('mobile.common.matches')}
        </h1>
        <button
          onClick={() => setMode(mode === 'league' ? 'tournaments' : 'league')}
          aria-pressed={mode === 'league'}
          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold flex-shrink-0 active:scale-[0.97] transition-transform"
          style={{
            background: mode === 'league' ? '#14b8a6' : '#fff',
            color: '#0f1611',
            border: `1px solid ${mode === 'league' ? '#14b8a6' : '#e7eae3'}`,
          }}
        >
          <span
            className="inline-block rounded-full"
            style={{ width: 8, height: 8, background: '#14b8a6' }}
          />
          {tr('mobile.league.title')}
          {leagueOpen > 0 && (
            <span
              className="font-mono text-[10px] font-bold px-1.5 rounded-full"
              style={{ background: '#0f1611', color: '#fff' }}
            >
              {leagueOpen}
            </span>
          )}
        </button>
      </div>

      {mode === 'league' && (
        <MobileSeriespill
          member={member}
          effectivePlayerDB={db}
          live={leagueLive}
          past={leaguePast}
          courtMeta={courtMeta}
          calcPlayerPositionInTournament={calcPlayerPositionInTournament}
          recordKMResultForTournament={recordKMResultForTournament}
          embedded
        />
      )}

      {mode === 'tournaments' && (
        <>
          <Segments<Seg>
            value={seg}
            onChange={setSeg}
            options={[
              {
                id: 'upcoming',
                label: tr('mobile.reservations.upcoming'),
                count: openCount + planned.length,
              },
              { id: 'played', label: tr('mobile.matches.played'), count: playedAll.length },
              // Beside the club's two segments, which are unchanged.
              ...(acrossClubs
                ? [{ id: 'all' as const, label: tr('mobile.history.allClubs') }]
                : []),
            ]}
          />

          {seg === 'all' && <AcrossClubsMatches />}

          {/* Kommende = tournaments in progress (your matches) + planned single matches */}
          {seg === 'upcoming' && live.length > 0 && (
            // Live tournaments two-up on a wide screen (desktop step 4).
            <div className="space-y-4 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-4 @wide:items-start">
              {live.map((l) => (
                <LiveTournamentBlock
                  key={l.key}
                  live={l}
                  member={member}
                  db={db}
                  courtMeta={courtMeta}
                  recordResultForTournament={recordResultForTournament}
                  recordKMResultForTournament={recordKMResultForTournament}
                />
              ))}
            </div>
          )}
          {seg === 'upcoming' &&
            (planned.length === 0 ? (
              live.length === 0 && (
                <EmptyState
                  icon={<Clock size={22} />}
                  title={tr('mobile.matches.emptyUpcoming')}
                  hint={tr('mobile.matches.emptyUpcomingHint')}
                />
              )
            ) : (
              // Planned matches two-up; the «single matches» label across both.
              <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start @wide:[&>.font-mono]:col-span-2">
                {live.length > 0 && (
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 px-1 pt-1">
                    {tr('mobile.matches.singleMatches')}
                  </div>
                )}
                {planned.map((mb) => (
                  <PlannedMatchCard
                    key={mb.plan.id}
                    plan={mb.plan}
                    booking={mb.booking}
                    member={member}
                    db={db}
                    fn={fn}
                    courtMeta={courtMeta}
                    busy={busyPlan === mb.plan.id}
                    canEdit={!!updatePlannedMatchPlayers}
                    onAdd={(team, pos) => setPickSlot({ planId: mb.plan.id, team, pos })}
                    onRemove={(team, pos) => setSlot(mb.plan, team, pos, null)}
                    onComplete={
                      completePlannedMatch
                        ? (sets) => completePlannedMatch(mb.plan.id, { sets, playedAt: Date.now() })
                        : undefined
                    }
                  />
                ))}
              </div>
            ))}

          {seg === 'played' &&
            (playedAll.length === 0 ? (
              <EmptyState
                icon={<Medal size={22} />}
                title={tr('mobile.matches.emptyPlayed')}
                hint={tr('mobile.matches.emptyPlayedHint')}
              />
            ) : (
              // Played matches and tournaments two-up on a wide screen.
              <div className="space-y-2 @wide:space-y-0 @wide:grid @wide:grid-cols-2 @wide:gap-2 @wide:items-start">
                {playedAll.map((it) =>
                  it.kind === 'single' ? (
                    <SingleMatchRow
                      key={`s:${it.pm.match.id}`}
                      pm={it.pm}
                      db={db}
                      meId={member?.id}
                    />
                  ) : (
                    <TournamentRow key={`t:${it.h.key}`} h={it.h} />
                  ),
                )}
              </div>
            ))}
        </>
      )}

      {pickSlot && (
        <MemberPicker
          players={db.filter((p: any) => {
            const plan = planned.find((mb) => mb.plan.id === pickSlot.planId)?.plan;
            return plan ? !isMemberInMatch(plan, p.id) : true;
          })}
          currentId={null}
          title={tr('mobile.matches.addPlayer')}
          hint={tr('mobile.matches.addPlayerHint')}
          onPick={async (id) => {
            const plan = planned.find((mb) => mb.plan.id === pickSlot.planId)?.plan;
            setPickSlot(null);
            if (plan) await setSlot(plan, pickSlot.team, pickSlot.pos, id);
          }}
          onClose={() => setPickSlot(null)}
        />
      )}
    </div>
  );
}

// ── A played single match, from the member's side ──────────────────
function SingleMatchRow({ pm, db, meId }: { pm: any; db: any[]; meId: any }) {
  const { t, formatDate } = useLocale();
  const m = pm.match;
  const me = pm.myTeam === 1 ? m.team1 : m.team2;
  const them = pm.myTeam === 1 ? m.team2 : m.team1;
  const sets = (m.sets || []).map((s: any) =>
    pm.myTeam === 1 ? `${s.games1}-${s.games2}` : `${s.games2}-${s.games1}`,
  );
  const won = pm.won;
  return (
    <Card accent={won == null ? '#8a948c' : won ? '#84cc16' : '#f43f5e'}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          {t('mobile.reservations.singleMatch')}
          {m.playedAt
            ? ` · ${formatDate(new Date(m.playedAt), { day: 'numeric', month: 'short' })}`
            : ''}
        </span>
        <span
          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
          style={{
            background: won == null ? '#eef0ea' : won ? '#f7fdde' : '#ffe4e6',
            color: won == null ? '#4a544c' : won ? '#3f6212' : '#be123c',
          }}
        >
          {won == null
            ? t('mobile.matches.playedTag')
            : won
              ? t('mobile.matches.wonTag')
              : t('mobile.matches.lostTag')}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="text-[13px] font-semibold text-ink truncate">
          <TeamNames team={me} db={db} meId={meId} />
        </div>
        <div className="font-mono font-bold text-[14px] text-ink tabular-nums flex-shrink-0">
          {sets.join('  ')}
        </div>
        <div className="text-[13px] text-ink-2 truncate text-right">
          <TeamNames team={them} db={db} meId={meId} />
        </div>
      </div>
      {pm.eloChange != null && (
        <div
          className="text-[11px] font-mono font-bold mt-1"
          style={{ color: pm.eloChange >= 0 ? '#3f6212' : '#be123c' }}
        >
          {pm.eloChange >= 0 ? '+' : ''}
          {Math.round(pm.eloChange)} ELO
        </div>
      )}
    </Card>
  );
}

// ── A finished tournament with the member's placement ───────────────
function TournamentRow({ h }: { h: any }) {
  const { t, formatDate } = useLocale();
  return (
    <Card accent={h.tt?.color || '#c2f02f'}>
      <div className="flex items-center gap-3">
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-2xl font-display font-semibold"
          style={{ width: 46, height: 46, background: '#eef0ea', color: '#0f1611', fontSize: 18 }}
        >
          {h.position ? `${h.position}.` : '–'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-ink truncate">{h.name}</div>
          <div className="text-[11px] text-ink-3">
            {h.tt?.label}
            {h.when
              ? ` · ${formatDate(new Date(h.when), { day: 'numeric', month: 'short', year: 'numeric' })}`
              : ''}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {h.position && h.total ? (
            <div className="text-[11px] text-ink-2">
              {t('mobile.matches.ofTotal', { total: h.total })}
            </div>
          ) : null}
          <div className="text-[10px] text-ink-3">
            {t('mobile.league.matchCount', { count: h.matches })}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── A planned single match on a booked court: four slots, editable ─
// The member can fill an open slot with any club member (or empty a slot
// again) as long as they are in the match themselves.
function PlannedMatchCard({
  plan: p,
  booking: b,
  member,
  db,
  fn,
  courtMeta,
  busy,
  canEdit,
  onAdd,
  onRemove,
  onComplete,
}: {
  plan: any;
  booking: any;
  member: any;
  db: any[];
  fn: (id: any) => string;
  courtMeta: any;
  busy: boolean;
  canEdit: boolean;
  onAdd: (team: 1 | 2, pos: 'p1Id' | 'p2Id') => void;
  onRemove: (team: 1 | 2, pos: 'p1Id' | 'p2Id') => void;
  /** Present when results can be recorded from the app. */
  onComplete?: (sets: { games1: number; games2: number }[]) => Promise<any>;
}) {
  const t = useT();
  const mine = isMemberInMatch(p, member?.id);
  const editable = canEdit && mine && !busy;
  const open = planOpenSlots(p);
  // Result entry: games per set, team 1 (left) – team 2 (right).
  const [scoring, setScoring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [sets, setSets] = useState([
    { g1: '', g2: '' },
    { g1: '', g2: '' },
    { g1: '', g2: '' },
  ]);
  const canScore = !!onComplete && mine && open === 0 && !busy;
  const parsedSets = sets
    .map((s) => ({ games1: parseInt(s.g1, 10), games2: parseInt(s.g2, 10) }))
    .filter((s) => !Number.isNaN(s.games1) && !Number.isNaN(s.games2));
  const setsWon = parsedSets.reduce(
    (acc, s) => {
      if (s.games1 > s.games2) acc[0] += 1;
      else if (s.games2 > s.games1) acc[1] += 1;
      return acc;
    },
    [0, 0],
  );
  const saveScore = async () => {
    if (!onComplete) return;
    if (parsedSets.length === 0 || setsWon[0] === setsWon[1]) {
      setScoreError(t('mobile.matches.needOneSet'));
      return;
    }
    setSaving(true);
    setScoreError(null);
    try {
      const r = await onComplete(parsedSets);
      if (r && r.success === false) setScoreError(r.error || t('mobile.matches.saveFailed'));
      else setScoring(false);
    } catch {
      setScoreError(t('mobile.matches.saveFailed'));
    } finally {
      setSaving(false);
    }
  };
  const Slot = ({ team, pos }: { team: 1 | 2; pos: 'p1Id' | 'p2Id' }) => {
    const id = (team === 1 ? p.team1 : p.team2)?.[pos] ?? null;
    const isMe = id != null && member && String(id) === String(member.id);
    if (id == null) {
      return editable ? (
        <button
          onClick={() => onAdd(team, pos)}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold"
          style={{ background: '#f7fdde', color: '#3f6212', border: '1px dashed #a3d117' }}
          aria-label={t('mobile.matches.addPlayer')}
        >
          <Plus size={12} /> {t('mobile.matches.add')}
        </button>
      ) : (
        <span className="text-ink-3 font-normal">{t('mobile.common.openSlot')}</span>
      );
    }
    return (
      <span className="inline-flex items-center gap-0.5">
        <PlayerName db={db} id={id} meId={member?.id} />
        {editable && !isMe && (
          <button
            onClick={() => onRemove(team, pos)}
            aria-label={t('mobile.matches.removePlayer', { name: fn(id) })}
            className="text-ink-3 hover:text-rose ml-0.5"
          >
            <X size={12} />
          </button>
        )}
      </span>
    );
  };
  return (
    <Card accent={open > 0 ? '#fde68a' : '#0ea5e9'}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          {relativeDayLabel(b.date)} · {b.startTime}–{b.endTime}
        </span>
        {open > 0 && (
          <span
            className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: '#fef9c3', color: '#854d0e' }}
          >
            {open === 1
              ? t('mobile.matches.openTag', { count: open })
              : t('mobile.matches.openTagPlural', { count: open })}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5 text-[13px] font-semibold text-ink">
        <div className="flex items-center gap-1 flex-wrap min-w-0">
          <Slot team={1} pos="p1Id" />
          <span className="text-ink-3">/</span>
          <Slot team={1} pos="p2Id" />
        </div>
        <span className="font-mono text-[11px] text-ink-3 flex-shrink-0">vs</span>
        <div className="flex items-center gap-1 flex-wrap justify-end min-w-0">
          <Slot team={2} pos="p1Id" />
          <span className="text-ink-3">/</span>
          <Slot team={2} pos="p2Id" />
        </div>
      </div>
      <div className="text-[11px] text-ink-3 flex items-center gap-1 mt-1.5">
        <MapPin size={11} /> {courtDisplayName(b.court, courtMeta || {})}
        {mine && open > 0 && canEdit && (
          <span className="ml-auto">{t('mobile.matches.fillSlotsHint')}</span>
        )}
        {p.liveScore?.sets?.length ? (
          <span className="ml-auto font-mono text-lime-deep font-bold">
            {p.liveScore.sets.map((s: any) => `${s.t1}-${s.t2}`).join(' ')}
          </span>
        ) : null}
      </div>

      {canScore && !scoring && (
        <button
          onClick={() => setScoring(true)}
          className="mt-3 w-full rounded-xl py-2.5 text-[13px] font-semibold active:scale-[0.98] transition-transform"
          style={{ background: '#0f1611', color: '#fff' }}
        >
          {t('mobile.matches.enterResult')}
        </button>
      )}
      {scoring && (
        <div className="mt-3 rounded-2xl p-3 space-y-2" style={{ background: '#f7f8f5' }}>
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-ink-3">
            <span>
              {fn(p.team1?.p1Id)}/{fn(p.team1?.p2Id)}
            </span>
            <span>
              {fn(p.team2?.p1Id)}/{fn(p.team2?.p2Id)}
            </span>
          </div>
          {sets.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[11px] text-ink-3 w-10">
                {t('mobile.matches.setNo', { n: i + 1 })}
              </span>
              <input
                inputMode="numeric"
                value={s.g1}
                onChange={(e) =>
                  setSets((prev) =>
                    prev.map((x, j) => (j === i ? { ...x, g1: e.target.value } : x)),
                  )
                }
                className="w-12 rounded-xl text-center font-mono font-bold text-[16px] py-1.5 tabular-nums"
                style={{
                  background: '#fff',
                  border: '1px solid var(--line-strong)',
                  color: '#0f1611',
                }}
                aria-label={t('mobile.matches.setTeamAria', { n: i + 1, team: 1 })}
              />
              <span className="text-ink-3">–</span>
              <input
                inputMode="numeric"
                value={s.g2}
                onChange={(e) =>
                  setSets((prev) =>
                    prev.map((x, j) => (j === i ? { ...x, g2: e.target.value } : x)),
                  )
                }
                className="w-12 rounded-xl text-center font-mono font-bold text-[16px] py-1.5 tabular-nums"
                style={{
                  background: '#fff',
                  border: '1px solid var(--line-strong)',
                  color: '#0f1611',
                }}
                aria-label={t('mobile.matches.setTeamAria', { n: i + 1, team: 2 })}
              />
              {i === 2 && (
                <span className="text-[10px] text-ink-3">
                  {t('mobile.matches.superTieBreakNote')}
                </span>
              )}
            </div>
          ))}
          {scoreError && (
            <div
              className="rounded-xl px-3 py-2 text-[12px]"
              style={{ background: '#ffe4e6', color: '#be123c' }}
            >
              {scoreError}
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => {
                setScoring(false);
                setScoreError(null);
              }}
              className="flex-1 rounded-xl py-2 text-[12px] font-semibold"
              style={{
                background: '#fff',
                border: '1px solid var(--line-strong)',
                color: '#0f1611',
              }}
            >
              {t('mobile.common.cancel')}
            </button>
            <button
              onClick={saveScore}
              disabled={saving}
              className="flex-1 rounded-xl py-2 text-[12px] font-semibold flex items-center justify-center gap-1 disabled:opacity-40"
              style={{ background: '#c2f02f', color: '#0f1611' }}
            >
              <Check size={14} strokeWidth={2.5} />{' '}
              {saving ? t('mobile.common.saving') : t('mobile.matches.saveResult')}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── One live tournament: header + the member's matches ──────────────
export function LiveTournamentBlock({
  live,
  member,
  db,
  courtMeta,
  recordResultForTournament,
  recordKMResultForTournament,
}: {
  live: LiveTournament;
  member: any;
  db: any[];
  courtMeta: any;
  recordResultForTournament?: MobileMatchesProps['recordResultForTournament'];
  recordKMResultForTournament?: MobileMatchesProps['recordKMResultForTournament'];
}) {
  // `tr`: `t` is the tournament here.
  const tr = useT();
  const t = live.tournament;
  const tt = getTournamentType(t.config || t.type);
  // Seriespill shares the KM match shape (pairs, sets) and the KM recorder.
  const isKM = live.type === 'klubbmesterskap' || live.type === 'seriespill';
  const canRecord =
    live.started && (isKM ? !!recordKMResultForTournament : !!recordResultForTournament);
  const ordered = [...live.open, ...live.played];

  return (
    <div>
      <div className="flex items-center gap-3 px-1 mb-2">
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-2xl"
          style={{ width: 40, height: 40, background: tt.color + '33', color: '#0f1611' }}
        >
          <Trophy size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="font-display font-semibold text-ink leading-tight truncate"
            style={{ fontSize: 17 }}
          >
            {t.config?.name || t.name || tt.label}
          </div>
          <div className="text-[11px] text-ink-3">
            {tt.label}
            {live.started ? tr('mobile.matches.inProgress') : tr('mobile.matches.notStarted')}
            {live.open.length > 0
              ? tr('mobile.matches.leftForYou', { count: live.open.length })
              : tr('mobile.matches.allPlayedSuffix')}
          </div>
        </div>
        <span
          className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded"
          style={{ background: tt.color, color: tt.text || '#0f1611' }}
        >
          LIVE
        </span>
      </div>
      {ordered.length === 0 ? (
        <EmptyState title={tr('mobile.matches.noneScheduled')} />
      ) : (
        <div className="space-y-2">
          {ordered.map((m: any) => (
            <LiveMatchCard
              key={m.id}
              match={m}
              tournament={t}
              isKM={isKM}
              member={member}
              db={db}
              courtMeta={courtMeta}
              canRecord={canRecord}
              accent={tt.color}
              onRecord={(s1, s2) => recordResultForTournament?.(t.id, m.id, s1, s2, 'mobile')}
              onRecordKM={(score) => {
                // Write back the RAW match (the history copy is normalized).
                const raw = (t.matches || []).find((x: any) => x.id === m.id) || m;
                recordKMResultForTournament?.(t.id, { ...raw, score });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LiveMatchCard({
  match: m,
  isKM,
  member,
  db,
  courtMeta,
  canRecord,
  accent,
  onRecord,
  onRecordKM,
}: {
  match: any;
  tournament: any;
  isKM: boolean;
  member: any;
  db: any[];
  courtMeta: any;
  canRecord: boolean;
  /** The tournament format's colour — keeps Padel24 and Seriespill visibly apart. */
  accent: string;
  onRecord: (s1: number, s2: number) => void;
  onRecordKM: (score: any) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  // «Registrer resultat» goes away when it is pressed, so the score field
  // takes focus — by an effect on that press rather than `autoFocus` (#900):
  // the same keyboard result, and never a focus move on a mount.
  const firstScoreRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (editing) firstScoreRef.current?.focus();
  }, [editing]);
  const [s1, setS1] = useState('');
  const [s2, setS2] = useState('');
  const [sets, setSets] = useState([
    { t1: '', t2: '' },
    { t1: '', t2: '' },
    { t1: '', t2: '' },
  ]);
  /**
   * A REFRESH FROM A BANEPAD MUST NOT DISCARD THIS EITHER (#556).
   *
   * Found by that change's own tripwire rather than by reading around — the
   * member app enters results into the same tournament document the portal and
   * the pads share, so an adoption can replace what a player is halfway through
   * typing on their phone. `sets` counts too: KM is entered set by set.
   */
  useUnsavedMatchScore(
    m?.id,
    editing && (s1 !== '' || s2 !== '' || sets.some((set) => set.t1 !== '' || set.t2 !== '')),
  );

  /**
   * The stored result as a VALUE, extracted so the dependency below can be
   * checked statically — and so it is obvious that the object is never the
   * dependency. See resultFingerprint for why that matters.
   */
  const storedResult = resultFingerprint(m);

  /**
   * THE AUTHORITATIVE RESULT WON, SO THE DRAFT GOES (#559 review).
   *
   * The member app writes into the same tournament document the portal and the
   * pads share, so the same acceptance applies: once the stored result changes —
   * which only happens here after somebody accepted it — the half-typed score on
   * the phone must not survive as if it were still in play. `editing` closes with
   * it, because the match now has a result.
   */
  useEffect(() => {
    setS1('');
    setS2('');
    setSets([
      { t1: '', t2: '' },
      { t1: '', t2: '' },
      { t1: '', t2: '' },
    ]);
    setEditing(false);
    // VALUE-SEMANTIC, NEVER THE OBJECT (#559 review). A KM/seriespill result is
    // an OBJECT, and every refresh is freshly parsed JSON — so `m?.score` gets a
    // new identity on every adoption, including one for a different court. That
    // fired this effect and cleared a draft nobody asked to discard: precisely
    // the silent loss the withheld-adoption rule exists to prevent, walked back
    // in through a reference comparison.
  }, [m?.id, storedResult]);
  const done = isMatchCompleted(m);
  const mySide = matchSideOf(m, member?.id);
  const meTeam = mySide === 2 ? m.team2 : m.team1;
  const themTeam = mySide === 2 ? m.team1 : m.team2;
  const myScore = isKM ? m.score?.t1 : m.score1;
  const theirScore = isKM ? m.score?.t2 : m.score2;
  const [meS, themS] = mySide === 2 ? [theirScore, myScore] : [myScore, theirScore];
  const won = done && meS != null && themS != null ? meS > themS : null;
  const kmSets = isKM && m.score?.sets ? m.score.sets : null;

  const meta = [
    m.round != null ? t('mobile.common.round', { round: String(m.round) }) : null,
    m.groupName || m.groupId || null,
    m.court != null ? courtDisplayName(m.court, courtMeta || {}) : null,
    m.startTime || null,
  ]
    .filter(Boolean)
    .join(' · ');

  const kmTally = () => {
    let t1 = 0;
    let t2 = 0;
    const valid = sets
      .map((s) => ({ t1: parseInt(s.t1, 10), t2: parseInt(s.t2, 10) }))
      .filter((s) => !Number.isNaN(s.t1) && !Number.isNaN(s.t2));
    valid.forEach((s) => {
      if (s.t1 > s.t2) t1 += 1;
      else if (s.t2 > s.t1) t2 += 1;
    });
    return { t1, t2, sets: valid };
  };

  const save = () => {
    if (isKM) {
      const tally = kmTally();
      if (tally.sets.length === 0) return;
      onRecordKM(tally);
    } else {
      const a = parseInt(s1, 10);
      const b = parseInt(s2, 10);
      if (Number.isNaN(a) || Number.isNaN(b)) return;
      // Inputs are «meg – dem»; the engine wants team1 – team2.
      const [t1, t2] = mySide === 2 ? [b, a] : [a, b];
      onRecord(t1, t2);
    }
    setEditing(false);
  };

  const inputCls =
    'w-12 rounded-xl text-center font-mono font-bold text-[16px] py-1.5 tabular-nums';
  const inputStyle = {
    background: '#fff',
    border: '1px solid var(--line-strong)',
    color: '#0f1611',
  };

  return (
    <Card accent={done ? (won ? '#84cc16' : won === false ? '#f43f5e' : '#8a948c') : accent}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
          {meta || (done ? t('mobile.matches.played') : t('mobile.matches.nextMatch'))}
        </span>
        {done ? (
          <span
            className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{
              background: won ? '#f7fdde' : won === false ? '#ffe4e6' : '#eef0ea',
              color: won ? '#3f6212' : won === false ? '#be123c' : '#4a544c',
            }}
          >
            {m.score?.walkover
              ? t('mobile.matches.walkoverTag')
              : won
                ? t('mobile.matches.wonTag')
                : won === false
                  ? t('mobile.matches.lostTag')
                  : t('mobile.matches.playedTag')}
          </span>
        ) : (
          <span
            className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: '#eef0ea', color: '#4a544c' }}
          >
            {t('mobile.matches.notPlayedTag')}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="text-[13px] font-semibold text-ink truncate">
          <TeamNames team={meTeam} db={db} meId={member?.id} />
        </div>
        <div className="font-mono font-bold text-[15px] text-ink tabular-nums flex-shrink-0">
          {done
            ? kmSets
              ? kmSets
                  .map((s: any) => (mySide === 2 ? `${s.t2}-${s.t1}` : `${s.t1}-${s.t2}`))
                  .join('  ')
              : `${meS ?? '–'} – ${themS ?? '–'}`
            : 'vs'}
        </div>
        <div className="text-[13px] text-ink-2 truncate text-right">
          <TeamNames team={themTeam} db={db} meId={member?.id} />
        </div>
      </div>

      {!done && canRecord && !editing && (
        <button
          onClick={() => setEditing(true)}
          className="mt-3 w-full rounded-xl py-2.5 text-[13px] font-semibold active:scale-[0.98] transition-transform"
          style={{ background: '#0f1611', color: '#fff' }}
        >
          {t('mobile.matches.enterResult')}
        </button>
      )}
      {!done && !canRecord && (
        <div
          className="mt-3 rounded-xl px-3 py-2 text-[11px] text-ink-3"
          style={{ background: '#eef0ea' }}
        >
          {t('mobile.matches.organiserRecords')}
        </div>
      )}

      {editing && (
        <div className="mt-3 rounded-2xl p-3 space-y-2" style={{ background: '#f7f8f5' }}>
          {isKM ? (
            <>
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                {t('mobile.matches.setsYouThem')}
              </div>
              {sets.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[11px] text-ink-3 w-10">
                    {t('mobile.matches.setNo', { n: i + 1 })}
                  </span>
                  <input
                    inputMode="numeric"
                    value={mySide === 2 ? s.t2 : s.t1}
                    onChange={(e) =>
                      setSets((prev) =>
                        prev.map((x, j) =>
                          j === i ? { ...x, [mySide === 2 ? 't2' : 't1']: e.target.value } : x,
                        ),
                      )
                    }
                    className={inputCls}
                    style={inputStyle}
                    aria-label={t('mobile.matches.setYouAria', { n: i + 1 })}
                  />
                  <span className="text-ink-3">–</span>
                  <input
                    inputMode="numeric"
                    value={mySide === 2 ? s.t1 : s.t2}
                    onChange={(e) =>
                      setSets((prev) =>
                        prev.map((x, j) =>
                          j === i ? { ...x, [mySide === 2 ? 't1' : 't2']: e.target.value } : x,
                        ),
                      )
                    }
                    className={inputCls}
                    style={inputStyle}
                    aria-label={t('mobile.matches.setThemAria', { n: i + 1 })}
                  />
                </div>
              ))}
            </>
          ) : (
            <div className="flex items-center justify-center gap-3">
              <div className="text-center">
                <div className="text-[10px] text-ink-3 mb-1">{t('mobile.matches.you')}</div>
                <input
                  inputMode="numeric"
                  value={s1}
                  onChange={(e) => setS1(e.target.value)}
                  className={inputCls}
                  style={inputStyle}
                  aria-label={t('mobile.matches.pointsYou')}
                  ref={firstScoreRef}
                />
              </div>
              <span className="text-ink-3 pt-4">–</span>
              <div className="text-center">
                <div className="text-[10px] text-ink-3 mb-1">{t('mobile.matches.them')}</div>
                <input
                  inputMode="numeric"
                  value={s2}
                  onChange={(e) => setS2(e.target.value)}
                  className={inputCls}
                  style={inputStyle}
                  aria-label={t('mobile.matches.pointsThem')}
                />
              </div>
            </div>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => setEditing(false)}
              className="flex-1 rounded-xl py-2 text-[12px] font-semibold"
              style={{
                background: '#fff',
                border: '1px solid var(--line-strong)',
                color: '#0f1611',
              }}
            >
              {t('mobile.common.cancel')}
            </button>
            <button
              onClick={save}
              className="flex-1 rounded-xl py-2 text-[12px] font-semibold flex items-center justify-center gap-1"
              style={{ background: '#c2f02f', color: '#0f1611' }}
            >
              <Check size={14} strokeWidth={2.5} /> {t('mobile.common.save')}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
